import { useState } from 'react'
import { BookOpen, Trophy, Target, TrendingUp, Lock, CheckCircle } from 'lucide-react'

const Learn = () => {
  const [selectedModule, setSelectedModule] = useState(null)

  const modules = [
    {
      id: 'basics',
      title: 'Betting Basics',
      description: 'Learn fundamental concepts of sports betting',
      lessons: 8,
      completed: 5,
      xp: 100,
      icon: BookOpen,
      locked: false
    },
    {
      id: 'nba',
      title: 'NBA Analytics',
      description: 'Deep dive into NBA stats and betting strategies',
      lessons: 12,
      completed: 3,
      xp: 150,
      icon: Trophy,
      locked: false
    },
    {
      id: 'advanced',
      title: 'Advanced Strategies',
      description: 'Professional betting techniques and bankroll management',
      lessons: 10,
      completed: 0,
      xp: 200,
      icon: Target,
      locked: false
    },
    {
      id: 'live',
      title: 'Live Betting',
      description: 'Master in-game betting and real-time analysis',
      lessons: 6,
      completed: 0,
      xp: 175,
      icon: TrendingUp,
      locked: true
    }
  ]

  const currentLesson = {
    title: 'Understanding Point Spreads',
    progress: 65,
    nextReward: 25,
    content: [
      'Point spreads are the most common way to bet on basketball and football.',
      'The spread levels the playing field between two mismatched teams.',
      'The favorite gives points, while the underdog receives points.',
      'To win a spread bet, the favorite must win by more than the spread.'
    ]
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-white">Learn & Earn</h1>

      {/* Current Progress */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <h2 className="text-xl font-bold mb-4">Continue Learning</h2>
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <p className="font-medium">{currentLesson.title}</p>
            <span className="text-sm">{currentLesson.progress}%</span>
          </div>
          <div className="bg-white/20 rounded-full h-3">
            <div
              className="bg-white rounded-full h-3 transition-all"
              style={{width: `${currentLesson.progress}%`}}
            ></div>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-sm opacity-90">
            Complete this lesson to earn {currentLesson.nextReward} XP
          </p>
          <button className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors">
            Continue Lesson
          </button>
        </div>
      </div>

      {/* Learning Modules */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modules.map((module) => {
          const Icon = module.icon
          const progressPercent = (module.completed / module.lessons) * 100

          return (
            <div
              key={module.id}
              className={`bg-dark-card border border-dark-border rounded-lg p-6 ${
                module.locked ? 'opacity-50' : 'hover:border-primary-500/50 cursor-pointer'
              } transition-all`}
              onClick={() => !module.locked && setSelectedModule(module)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    module.locked ? 'bg-gray-700' : 'bg-primary-500/20'
                  }`}>
                    {module.locked ? (
                      <Lock className="h-6 w-6 text-gray-400" />
                    ) : (
                      <Icon className="h-6 w-6 text-primary-500" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{module.title}</h3>
                    <p className="text-sm text-gray-400">{module.description}</p>
                  </div>
                </div>
                {progressPercent === 100 && (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Progress</span>
                    <span className="text-white">
                      {module.completed}/{module.lessons} lessons
                    </span>
                  </div>
                  <div className="bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full transition-all"
                      style={{width: `${progressPercent}%`}}
                    ></div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-2">
                  <span className="text-sm text-gray-400">
                    +{module.xp} XP on completion
                  </span>
                  {!module.locked && (
                    <button className="text-primary-500 text-sm font-medium hover:text-primary-400">
                      Start Learning →
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Daily Tips */}
      <div className="bg-dark-card border border-dark-border rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">Today's Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="font-medium text-primary-500 mb-2">💡 Pro Tip</h3>
            <p className="text-sm text-gray-300">
              Always check injury reports 30 minutes before tip-off for last-minute changes.
            </p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="font-medium text-primary-500 mb-2">📊 Stats Insight</h3>
            <p className="text-sm text-gray-300">
              Home underdogs in divisional games cover the spread 58% of the time.
            </p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="font-medium text-primary-500 mb-2">⚠️ Warning</h3>
            <p className="text-sm text-gray-300">
              Avoid betting on teams playing back-to-back road games.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Learn